/**
 * Create Platform Member Modal — two-panel dialog for admin-created accounts.
 *
 * Left panel:  member details (email, name, password, verify/welcome toggles)
 * Right panel: subscription tier + cycle, family vault addon, initial credits
 *
 * Submits to apiService.createPlatformMember() which hits
 * POST /api/adminUserManagement/users.
 */

import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import { apiService } from '../../services/api';
import {
  SUBSCRIPTION_TIERS,
  BILLING_CYCLES,
  VAULT_TIERS,
  SubscriptionTier,
  BillingCycle,
  VaultTier,
  planCodeFor,
  priceFor,
  FALLBACK_SUBSCRIPTION_PLANS,
  SubscriptionPlan,
} from '../../config/subscriptionPlans';

interface CreatePlatformMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  plans?: SubscriptionPlan[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CreatePlatformMemberModal: React.FC<CreatePlatformMemberModalProps> = ({
  open,
  onClose,
  onSuccess,
  plans,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const planList = plans && plans.length ? plans : FALLBACK_SUBSCRIPTION_PLANS;

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [autoVerify, setAutoVerify] = useState(true);
  const [sendWelcome, setSendWelcome] = useState(false);

  const [tier, setTier] = useState<SubscriptionTier>('legacy_creator');
  const [cycle, setCycle] = useState<BillingCycle>('annual');
  const [vaultTier, setVaultTier] = useState<'none' | VaultTier>('none');
  const [vaultCycle, setVaultCycle] = useState<BillingCycle>('annual');
  const [credits, setCredits] = useState<number>(3);
  const [reason, setReason] = useState('Admin-created comp account for internal testing');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const planCode = useMemo(() => planCodeFor(tier, cycle), [tier, cycle]);
  const selectedTierMeta = SUBSCRIPTION_TIERS.find(t => t.value === tier)!;
  const tierPrice = useMemo(() => priceFor(planCode, planList), [planCode, planList]);

  const reset = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setShowPassword(false);
    setAutoVerify(true);
    setSendWelcome(false);
    setTier('legacy_creator');
    setCycle('annual');
    setVaultTier('none');
    setVaultCycle('annual');
    setCredits(3);
    setReason('Admin-created comp account for internal testing');
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const validate = (): string | null => {
    if (!EMAIL_RE.test(email)) return 'Enter a valid email address';
    if (!firstName.trim()) return 'First name is required';
    if (!lastName.trim()) return 'Last name is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (reason.trim().length < 10) return 'Reason must be at least 10 characters';
    return null;
  };

  const handleSubmit = async () => {
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await apiService.createPlatformMember({
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
        subscription_plan: planCode,
        family_vault_tier: vaultTier === 'none' ? undefined : vaultTier,
        family_vault_cycle: vaultTier === 'none' ? undefined : vaultCycle,
        chapter_credits: credits,
        auto_verify_email: autoVerify,
        send_welcome_email: sendWelcome,
        reason: reason.trim(),
      });
      dispatch(addNotification({
        type: 'success',
        message: `Created ${email} (${planCode}${vaultTier !== 'none' ? ` + vault_${vaultTier}` : ''})`,
      }));
      reset();
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create member';
      setError(msg);
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <PersonAddIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Create Platform Member</Typography>
          <Typography variant="caption" color="text.secondary">
            Admin-granted accounts bypass Stripe. The audit log flags these as comp_grant=true.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* ============== LEFT PANEL — Member Details ============== */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
              Member Details
            </Typography>

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              size="small"
              required
              sx={{ mb: 2 }}
              error={email.length > 0 && !EMAIL_RE.test(email)}
              helperText={email.length > 0 && !EMAIL_RE.test(email) ? 'Not a valid email' : ' '}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  size="small"
                  required
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              size="small"
              required
              sx={{ mb: 2 }}
              helperText={`${password.length}/8 characters minimum`}
              error={password.length > 0 && password.length < 8}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(v => !v)}>
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={autoVerify}
                  onChange={e => {
                    setAutoVerify(e.target.checked);
                    if (e.target.checked) setSendWelcome(false);
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-verify email</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Skip verification step — use for comp / internal accounts
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              sx={{ display: 'flex', mt: 1 }}
              control={
                <Switch
                  checked={sendWelcome}
                  onChange={e => setSendWelcome(e.target.checked)}
                  disabled={autoVerify}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Send welcome email</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Disabled when auto-verify is on (credentials shared manually)
                  </Typography>
                </Box>
              }
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reason (audit log)"
              value={reason}
              onChange={e => setReason(e.target.value)}
              size="small"
              required
              sx={{ mt: 2 }}
              helperText="Minimum 10 characters. Logged with comp_grant flag."
            />
          </Grid>

          {/* ============== RIGHT PANEL — Subscription & Vault ============== */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
              Subscription
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Tier
            </Typography>
            <ToggleButtonGroup
              value={tier}
              exclusive
              onChange={(_, v) => v && setTier(v)}
              size="small"
              sx={{ mb: 2, flexWrap: 'wrap' }}
            >
              {SUBSCRIPTION_TIERS.map(t => (
                <ToggleButton key={t.value} value={t.value} sx={{ textTransform: 'none', px: 1.5 }}>
                  {t.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {selectedTierMeta.hasCycle && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Billing Cycle
                </Typography>
                <ToggleButtonGroup
                  value={cycle}
                  exclusive
                  onChange={(_, v) => v && setCycle(v)}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  {BILLING_CYCLES.map(c => (
                    <ToggleButton key={c.value} value={c.value} sx={{ textTransform: 'none', px: 2 }}>
                      {c.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.25, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Chip size="small" label={planCode} color="primary" />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {Number.isFinite(tierPrice as number)
                  ? `$${Number(tierPrice).toFixed(2)}${cycle === 'monthly' ? '/mo' : '/yr'}`
                  : 'Free'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                comp_grant = {tier !== 'free' && tier !== 'trial' ? 'true' : 'false'}
              </Typography>
            </Box>

            <TextField
              fullWidth
              type="number"
              label="Starter chapter credits"
              value={credits}
              onChange={e => setCredits(Math.max(0, parseInt(e.target.value) || 0))}
              size="small"
              sx={{ mb: 3 }}
              inputProps={{ min: 0, max: 1000 }}
              helperText="Initial chapter_credits on the user row"
            />

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
              Family Vault Addon
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Tier
            </Typography>
            <ToggleButtonGroup
              value={vaultTier}
              exclusive
              onChange={(_, v) => v && setVaultTier(v)}
              size="small"
              sx={{ mb: 2, flexWrap: 'wrap' }}
            >
              {VAULT_TIERS.map(v => (
                <ToggleButton key={v.value} value={v.value} sx={{ textTransform: 'none', px: 1.5 }}>
                  {v.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {vaultTier !== 'none' && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Vault Billing Cycle
                </Typography>
                <ToggleButtonGroup
                  value={vaultCycle}
                  exclusive
                  onChange={(_, v) => v && setVaultCycle(v)}
                  size="small"
                >
                  {BILLING_CYCLES.map(c => (
                    <ToggleButton key={c.value} value={c.value} sx={{ textTransform: 'none', px: 2 }}>
                      {c.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
        >
          {submitting ? 'Creating…' : 'Create Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePlatformMemberModal;
